import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, getDocs, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  Container, Paper, Typography, Box, Button, TextField, List, ListItem, 
  ListItemText, Divider, Grid, Chip, LinearProgress, IconButton, Dialog, 
  DialogTitle, DialogContent, DialogActions, InputAdornment 
} from '@mui/material';
import { ArrowBack, TrendingUp, Add, Remove, CalendarToday, History, Edit, Delete } from '@mui/icons-material';
import { addMonths, addDays, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import FloatingCalculator from './FloatingCalculator';

function CaixinhaDetalhes({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caixinha, setCaixinha] = useState(null);
  const [historico, setHistorico] = useState([]);
  
  // Lançamento Novo
  const [valorOperacao, setValorOperacao] = useState('');
  const [descricao, setDescricao] = useState('');

  // Estados de Edição
  const [openEdit, setOpenEdit] = useState(false);
  const [itemEditando, setItemEditando] = useState(null);
  const [editDescricao, setEditDescricao] = useState('');
  const [editValorFormatado, setEditValorFormatado] = useState('');
  const [editData, setEditData] = useState('');

  const fetchDados = async () => {
    const docRef = doc(db, `users/${user.uid}/investimentos/${id}`);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) setCaixinha({ id: docSnap.id, ...docSnap.data() });

    const histRef = collection(db, `users/${user.uid}/investimentos/${id}/historico`);
    const q = query(histRef, orderBy('data', 'desc'));
    const histSnap = await getDocs(q);
    setHistorico(histSnap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => { fetchDados(); }, [id, user.uid]);

  const formatarMoeda = (value) => {
    let v = value.toString().replace(/\D/g, "");
    const res = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(parseFloat(v) / 100);
    return res === "NaN" ? "" : res;
  };

  // --- NOVA MOVIMENTAÇÃO ---
  const handleOperacao = async (tipo) => {
    const valor = parseFloat(valorOperacao.replace(/\./g, '').replace(',', '.'));
    if (!valor) return;

    const novoSaldo = tipo === 'deposito' ? caixinha.saldoAtual + valor : caixinha.saldoAtual - valor;
    
    await updateDoc(doc(db, `users/${user.uid}/investimentos/${id}`), { saldoAtual: novoSaldo });
    
    await addDoc(collection(db, `users/${user.uid}/investimentos/${id}/historico`), {
      tipo, 
      valor, 
      descricao: descricao || (tipo === 'deposito' ? 'Novo Aporte' : 'Resgate'),
      data: new Date().toISOString()
    });

    setValorOperacao(''); setDescricao(''); fetchDados();
  };

  // --- ABRIR EDIÇÃO ---
  const abrirEdicao = (item) => {
    setItemEditando(item);
    setEditDescricao(item.descricao);
    setEditValorFormatado(formatarMoeda(item.valor.toString()));
    setEditData(item.data.split('T')[0]); // Pega só o YYYY-MM-DD
    setOpenEdit(true);
  };

  // --- SALVAR EDIÇÃO COM RECÁLCULO ---
  const salvarEdicao = async () => {
    const novoValorNum = parseFloat(editValorFormatado.replace(/\./g, '').replace(',', '.'));
    if (!novoValorNum) return;

    // 1. Calcula a diferença para ajustar o saldo principal da Caixinha
    let diferencaSaldo = 0;
    if (itemEditando.tipo === 'deposito') {
      diferencaSaldo = novoValorNum - itemEditando.valor;
    } else { // se for retirada, aumentar o valor diminui o saldo
      diferencaSaldo = itemEditando.valor - novoValorNum;
    }

    // 2. Atualiza o Lançamento no Histórico
    const dataCompleta = new Date(editData + 'T12:00:00').toISOString();
    await updateDoc(doc(db, `users/${user.uid}/investimentos/${id}/historico/${itemEditando.id}`), {
      descricao: editDescricao,
      valor: novoValorNum,
      data: dataCompleta
    });

    // 3. Atualiza o Saldo da Caixinha
    await updateDoc(doc(db, `users/${user.uid}/investimentos/${id}`), { 
      saldoAtual: caixinha.saldoAtual + diferencaSaldo 
    });

    setOpenEdit(false); fetchDados();
  };

  // --- DELETAR MOVIMENTAÇÃO COM RECÁLCULO ---
  const excluirMovimentacao = async (item) => {
    if (window.confirm("Deseja apagar esta movimentação? O saldo da caixinha será recalculado.")) {
      
      let ajusteSaldo = item.tipo === 'deposito' ? -item.valor : item.valor;

      await deleteDoc(doc(db, `users/${user.uid}/investimentos/${id}/historico/${item.id}`));
      await updateDoc(doc(db, `users/${user.uid}/investimentos/${id}`), { 
        saldoAtual: caixinha.saldoAtual + ajusteSaldo 
      });

      fetchDados();
    }
  };

  const calcularPrevisao = () => {
    if (!caixinha || caixinha.meta <= caixinha.saldoAtual || !caixinha.aportePrevisto) return null;
    const falta = caixinha.meta - caixinha.saldoAtual;
    const qtdAportes = Math.ceil(falta / caixinha.aportePrevisto);
    const hoje = new Date();
    let dataFinal = caixinha.frequencia === 'mensal' ? addMonths(hoje, qtdAportes) : caixinha.frequencia === 'diario' ? addDays(hoje, qtdAportes) : null;
    return dataFinal ? format(dataFinal, "MMMM 'de' yyyy", { locale: ptBR }) : null;
  };

  if (!caixinha) return <Typography>Carregando...</Typography>;
  const previsao = calcularPrevisao();
  const progresso = caixinha.meta > 0 ? (caixinha.saldoAtual / caixinha.meta) * 100 : 0;

  return (
    <Container maxWidth="md" sx={{ mt: 1, px: { xs: 0.5, sm: 2 } }}>
      <FloatingCalculator />
      
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/investimentos')} sx={{ mb: 2, color: '#1a237e', fontWeight: 'bold' }}>
        Voltar
      </Button>

      {/* CARD DE RESUMO */}
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, bgcolor: 'white', mb: 2, borderLeft: '6px solid #1a237e' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', letterSpacing: 1, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
              SALDO ACUMULADO
            </Typography>
            <Typography sx={{ fontWeight: 'bold', color: '#1a237e', my: 1, fontSize: { xs: '1.3rem', sm: '2rem' } }}>
              R$ {caixinha.saldoAtual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
               <Chip label={caixinha.categoria.toUpperCase()} size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', fontSize: { xs: '0.65rem', sm: '0.8rem' } }} />
               {caixinha.meta > 0 && <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>Meta: R$ {caixinha.meta.toLocaleString('pt-BR')}</Typography>}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
             {caixinha.meta > 0 ? (
               <Box sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 2 }}>
                 <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                   <Typography variant="caption" fontWeight="bold" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>Progresso</Typography>
                   <Typography variant="caption" fontWeight="bold" color="#2e7d32" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{progresso.toFixed(0)}%</Typography>
                 </Box>
                 <LinearProgress variant="determinate" value={Math.min(progresso, 100)} sx={{ height: 8, borderRadius: 5, mb: 1, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: '#2e7d32' } }} />
                 {previsao && (
                   <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#1565c0', fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                     <CalendarToday fontSize="inherit" /> Conclusão: {previsao}
                   </Typography>
                 )}
               </Box>
             ) : (
               <Box sx={{ p: 1.5, border: '1px dashed #ccc', borderRadius: 2, textAlign: 'center' }}>
                 <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>Sem meta definida.</Typography>
               </Box>
             )}
          </Grid>
        </Grid>
      </Paper>

      {/* ÁREA DE OPERAÇÃO */}
      <Paper sx={{ p: { xs: 2, sm: 2.5 }, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold', color: '#333', fontSize: { xs: '0.85rem', sm: '1rem' } }}>
          REGISTRAR MOVIMENTAÇÃO
        </Typography>
        <Grid container spacing={1.5} alignItems="center">
           <Grid item xs={12} sm={4}>
             <TextField 
               fullWidth size="small" label="Valor" placeholder="0,00"
               value={valorOperacao} 
               onChange={e => setValorOperacao(formatarMoeda(e.target.value))} 
               InputProps={{ sx: { borderRadius: 1 }, startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
             />
           </Grid>
           <Grid item xs={12} sm={4}>
             <TextField fullWidth size="small" label="Descrição" placeholder="Ex: Aporte mensal" value={descricao} onChange={e => setDescricao(e.target.value)} InputProps={{ sx: { borderRadius: 1 } }} />
           </Grid>
           <Grid item xs={6} sm={2}>
             <Button fullWidth size="small" variant="contained" color="success" startIcon={<Add />} onClick={() => handleOperacao('deposito')} sx={{ borderRadius: 1, fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>
               GUARDAR
             </Button>
           </Grid>
           <Grid item xs={6} sm={2}>
             <Button fullWidth size="small" variant="outlined" color="warning" startIcon={<Remove />} onClick={() => handleOperacao('retirada')} sx={{ borderRadius: 1, fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>
               RESGATAR
             </Button>
           </Grid>
        </Grid>
      </Paper>

      {/* HISTÓRICO COM LÁPIS E LIXEIRA */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
        <Box sx={{ p: 1.5, bgcolor: '#f9fafb', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <History fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight="bold" color="textSecondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' } }}>EXTRATO DETALHADO</Typography>
        </Box>
        <List sx={{ p: 0 }}>
          {historico.map((h, index) => (
            <React.Fragment key={h.id}>
              <ListItem 
                sx={{ py: 1.25, px: { xs: 1, sm: 2 }, '&:hover': { bgcolor: '#f9f9f9' }, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Box sx={{ bgcolor: h.tipo === 'deposito' ? '#e8f5e9' : '#fff3e0', p: 0.75, borderRadius: '6px', minWidth: 28, display: 'flex', justifyContent: 'center' }}>
                  {h.tipo === 'deposito' ? <TrendingUp color="success" sx={{ fontSize: 18 }} /> : <Remove color="warning" sx={{ fontSize: 18 }} />}
                </Box>
                
                <ListItemText 
                  primary={<Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>{h.descricao}</Typography>}
                  secondary={<Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{new Date(h.data).toLocaleDateString('pt-BR')}</Typography>} 
                  sx={{ my: 0 }}
                />
                
                <Typography fontWeight="bold" color={h.tipo === 'deposito' ? 'green' : '#ed6c02'} sx={{ mr: 1, whiteSpace: 'nowrap', fontSize: { xs: '0.8rem', sm: '0.95rem' } }}>
                  {h.tipo === 'deposito' ? '+' : '-'} R$ {h.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </Typography>

                {/* Botões de Ação na Lateral */}
                <Box sx={{ display: 'flex', gap: 0 }}>
                  <IconButton size="small" onClick={() => abrirEdicao(h)} sx={{ p: 0.5 }}>
                    <Edit fontSize="small" color="primary" />
                  </IconButton>
                  <IconButton size="small" onClick={() => excluirMovimentacao(h)} sx={{ p: 0.5 }}>
                    <Delete fontSize="small" color="error" />
                  </IconButton>
                </Box>

              </ListItem>
              {index < historico.length - 1 && <Divider />}
            </React.Fragment>
          ))}
          {historico.length === 0 && <Typography sx={{ p: 4, textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>Nenhum histórico disponível.</Typography>}
        </List>
      </Paper>

      {/* MODAL DE EDIÇÃO */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white' }}>Editar Movimentação</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Descrição" value={editDescricao} onChange={(e) => setEditDescricao(e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField type="date" fullWidth label="Data" value={editData} onChange={(e) => setEditData(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField 
                fullWidth label="Valor" 
                value={editValorFormatado} 
                onChange={(e) => setEditValorFormatado(formatarMoeda(e.target.value))} 
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} 
              />
            </Grid>
            <Grid item xs={12}>
               <Typography variant="caption" color="textSecondary">
                 * Alterar o valor irá recalcular automaticamente o saldo atual da caixinha.
               </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenEdit(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarEdicao} sx={{ bgcolor: '#1a237e' }}>Salvar Alterações</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
}

export default CaixinhaDetalhes;